// @flow
import { call, put, takeLatest, all } from "redux-saga/effects";
import API from "api";

import * as ActionTypes from 'constants/ActionTypes';
import * as Actions from "actions/seller/seller.contacts";

function* uploadContacts(action: Object) {
  try {
    const response = yield API.post(
      `sellers/contacts/upload`,
      action.formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );
    // Extract the actual data from backend response structure
    const data = response.response || response.data?.response || response.data || {};
    yield put(Actions.uploadContactsSuccess(data));
  } catch (error) {
    yield put(Actions.uploadContactsFailure(error));
  }
}

function* getContacts(action: Object) {
  try {
    const params = action.payload || {};
    const queryString = new URLSearchParams(params).toString();
    const response = yield API.get(`sellers/contacts${queryString ? `?${queryString}` : ''}`, {});
    
    // Extract the actual data from backend response structure
    // Backend returns: { statusCode, statusMessage, status, response: {...} }
    const data = response.response || response.data?.response || response.data || {};
    
    yield put(Actions.getContactsSuccess(data));
  } catch (error) {
    yield put(Actions.getContactsFailure(error));
  }
}

function* getContactDetail(action: Object) {
  try {
    const { contactId } = action.payload;
    const response = yield API.get(`sellers/contacts/${contactId}`, {});
    // Extract the actual data from backend response structure
    const data = response.response || response.data?.response || response.data || {};
    yield put(Actions.getContactDetailSuccess(data));
  } catch (error) {
    yield put(Actions.getContactDetailFailure(error));
  }
}

function* createContact(action: Object) {
  try {
    const response = yield API.post(`sellers/contacts`, action.payload);
    // Extract the actual data from backend response structure
    const data = response.response || response.data?.response || response.data || {};
    yield put(Actions.createContactSuccess(data));
  } catch (error) {
    yield put(Actions.createContactFailure(error));
  }
}

function* updateContact(action: Object) {
  try {
    const { contactId, ...contactData } = action.payload;
    const response = yield API.put(`sellers/contacts/${contactId}`, contactData);
    // Extract the actual data from backend response structure
    const data = response.response || response.data?.response || response.data || {};
    yield put(Actions.updateContactSuccess(data));
  } catch (error) {
    yield put(Actions.updateContactFailure(error));
  }
}

function* deleteContact(action: Object) {
  try {
    const { contactId } = action.payload;
    yield API.delete(`sellers/contacts/${contactId}`, {});
    yield put(Actions.deleteContactSuccess({ contactId }));
  } catch (error) {
    yield put(Actions.deleteContactFailure(error));
  }
}

function* deleteMultipleContacts(action: Object) {
  try {
    const { contactIds } = action.payload;
    
    // Delete contacts in parallel
    yield all(
      contactIds.map(contactId =>
        call(API.delete, `sellers/contacts/${contactId}`, {})
      )
    );
    
    yield put(Actions.deleteMultipleContactsSuccess({ contactIds }));
  } catch (error) {
    yield put(Actions.deleteMultipleContactsFailure(error));
  }
}

function* getContactLists(action: Object) {
  try {
    const params = action.payload || {};
    const queryString = new URLSearchParams(params).toString();
    const response = yield API.get(`sellers/contacts/lists/all${queryString ? `?${queryString}` : ''}`, {});
    // Extract the actual data from backend response structure
    const data = response.response || response.data?.response || response.data || {};
    yield put(Actions.getContactListsSuccess(data));
  } catch (error) {
    yield put(Actions.getContactListsFailure(error));
  }
}

function* createContactList(action: Object) {
  try {
    const response = yield API.post(`sellers/contacts/lists`, action.payload);
    // Extract the actual data from backend response structure
    const data = response.response || response.data?.response || response.data || {};
    yield put(Actions.createContactListSuccess(data));
  } catch (error) {
    yield put(Actions.createContactListFailure(error));
  }
}

function* getSubscriptions(action: Object) {
  try {
    const params = action.payload || {};
    const queryString = new URLSearchParams(params).toString();
    const response = yield API.get(`sellers/contacts/subscriptions/all${queryString ? `?${queryString}` : ''}`, {});
    // Extract the actual data from backend response structure
    const data = response.response || response.data?.response || response.data || {};
    yield put(Actions.getSubscriptionsSuccess(data));
  } catch (error) {
    yield put(Actions.getSubscriptionsFailure(error));
  }
}

function* sendNotificationToContacts(action: Object) {
  try {
    const response = yield API.post(`sellers/contacts/send-notification`, action.payload);
    // Extract the actual data from backend response structure
    const data = response.response || response.data?.response || response.data || {};
    yield put(Actions.sendNotificationToContactsSuccess(data));
  } catch (error) {
    yield put(Actions.sendNotificationToContactsFailure(error));
  }
}

function* sendNotificationToList(action: Object) {
  try {
    const response = yield API.post(`sellers/contacts/send-notification-to-list`, action.payload);
    // Extract the actual data from backend response structure
    const data = response.response || response.data?.response || response.data || {};
    yield put(Actions.sendNotificationToListSuccess(data));
  } catch (error) {
    yield put(Actions.sendNotificationToListFailure(error));
  }
}

function* getMessageTemplates(action: Object) {
  try {
    const response = yield API.get(`sellers/contacts/message-templates`, {});
    // Extract the actual data from backend response structure
    const data = response.response || response.data?.response || response.data || {};
    yield put(Actions.getMessageTemplatesSuccess(data));
  } catch (error) {
    yield put(Actions.getMessageTemplatesFailure(error));
  }
}

export default function* contactsSaga() {
  yield takeLatest(ActionTypes.SELLER_UPLOAD_CONTACTS_START, uploadContacts);
  yield takeLatest(ActionTypes.SELLER_GET_CONTACTS_START, getContacts);
  yield takeLatest(ActionTypes.SELLER_GET_CONTACT_DETAIL_START, getContactDetail);
  yield takeLatest(ActionTypes.SELLER_CREATE_CONTACT_START, createContact);
  yield takeLatest(ActionTypes.SELLER_UPDATE_CONTACT_START, updateContact);
  yield takeLatest(ActionTypes.SELLER_DELETE_CONTACT_START, deleteContact);
  yield takeLatest(ActionTypes.SELLER_DELETE_MULTIPLE_CONTACTS_START, deleteMultipleContacts);
  yield takeLatest(ActionTypes.SELLER_GET_CONTACT_LISTS_START, getContactLists);
  yield takeLatest(ActionTypes.SELLER_CREATE_CONTACT_LIST_START, createContactList);
  yield takeLatest(ActionTypes.SELLER_GET_SUBSCRIPTIONS_START, getSubscriptions);
  yield takeLatest(ActionTypes.SELLER_SEND_NOTIFICATION_TO_CONTACTS_START, sendNotificationToContacts);
  yield takeLatest(ActionTypes.SELLER_SEND_NOTIFICATION_TO_LIST_START, sendNotificationToList);
  yield takeLatest(ActionTypes.SELLER_GET_MESSAGE_TEMPLATES_START, getMessageTemplates);
}

